const ServiceAgreement = () => {
  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 text-xs leading-tight text-gray-800">
      <h1 className="mb-3 text-xl font-bold">Regulify.AI Services Agreement</h1>
      <p className="text-xs leading-tight text-gray-600">
        This Agreement is a legally binding contract between you (the user) and
        RegulifyAI LLC (“Regulify.AI”) for accessing and using the
        Services. By accepting this Agreement, you agree to its terms. If you
        disagree, you may not use the Services.
      </p>

      {/* Section 1 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">1. Key Points</h2>
        <ul className="list-inside list-disc space-y-0.5">
          <li>
            You confirm you are at least 18 years old and authorized to accept
            on behalf of another party if applicable.
          </li>
          <li>
            Regulify.AI may update this Agreement and will notify you. Continued
            use signifies acceptance of changes.
          </li>
        </ul>
      </section>

      {/* Section 2 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">2. Defined Terms</h2>
        <ul className="list-inside list-disc space-y-0.5">
          <li>
            <strong>AI Tools:</strong> AI models and technologies.
          </li>
          <li>
            <strong>Applications:</strong> Software developed to work with
            Regulify.AI's APIs.
          </li>
          <li>
            <strong>Customer Data:</strong> Any data provided by you, including
            Customer Input.
          </li>
          <li>
            <strong>Customer Input:</strong> Text or data you or your users send
            through Regulify.AI APIs.
          </li>
          <li>
            <strong>Output:</strong> Results generated from Customer Input.
          </li>
          <li>
            <strong>Services:</strong> Regulify.AI’s online search engine and
            related APIs.
          </li>
          <li>
            <strong>Support Services:</strong> Optional services provided by
            Regulify.AI.
          </li>
        </ul>
      </section>

      {/* Section 3 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">3. Your Responsibilities</h2>
        <ul className="list-inside list-disc space-y-0.5">
          <li>
            <strong>Account:</strong> You must maintain secure access and not
            share credentials.
          </li>
          <li>
            <strong>General Restrictions:</strong> No misuse (e.g., reverse
            engineering, illegal use, overload).
          </li>
          <li>
            <strong>Setup:</strong> You are responsible for required
            hardware/software/internet.
          </li>
          <li>
            <strong>Your Applications & Data:</strong> Ensure compliance and
            security of your data.
          </li>
        </ul>
      </section>

      {/* Section 4 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">4. Fees</h2>
        <p>
          You agree to pay all service fees. Payments are non-refundable.
          Regulify.AI may change fees with notice. Late payments incur interest
          and potential collection costs.
        </p>
      </section>

      {/* Section 5 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">5. Confidentiality</h2>
        <p>
          Keep Regulify.AI’s confidential information secure and undisclosed.
          Upon termination, destroy or return such information.
        </p>
      </section>

      {/* Section 6 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">6. Customer Data</h2>
        <p>
          You retain ownership of your data but allow Regulify.AI to use it to
          provide and improve Services. You must have rights to share this data.
        </p>
      </section>

      {/* Section 7 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">7. Intellectual Property</h2>
        <ul className="list-inside list-disc space-y-0.5">
          <li>
            Regulify.AI retains all rights in the Services and enhancements.
          </li>
          <li>Feedback is assigned to Regulify.AI.</li>
          <li>
            Usage Data collected by Regulify.AI is owned solely by Regulify.AI.
          </li>
        </ul>
      </section>

      {/* Section 8 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">8. Indemnification</h2>
        <p>
          You agree to indemnify Regulify.AI against damages from your use,
          data, or violations. Regulify.AI may request you defend claims at your
          expense.
        </p>
      </section>

      {/* Section 9 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">9. Disclaimers</h2>
        <ul className="list-inside list-disc space-y-0.5">
          <li>Services are provided “as is.”</li>
          <li>
            Regulify.AI does not guarantee output accuracy, especially from AI
            tools. You are responsible for evaluating outputs.
          </li>
        </ul>
      </section>

      {/* Section 10 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">10. Third-Party Services</h2>
        <p>
          Some features rely on third-party services. Regulify.AI is not
          responsible for these and disclaims related liabilities. Use is at
          your own risk.
        </p>
      </section>

      {/* Section 11 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">
          11. Limitation of Liability
        </h2>
        <p>
          Regulify.AI is not liable for indirect or consequential damages. Total
          liability is limited to payments made in the 3 months before a claim.
        </p>
      </section>

      {/* Section 12 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">12. Term and Termination</h2>
        <p>
          The Agreement ends if you stop using the Services or Regulify.AI
          terminates it. After termination, you lose access and must delete
          related materials.
        </p>
      </section>

      {/* Section 13 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">
          13. Arbitration Agreement
        </h2>
        <ul className="list-inside list-disc space-y-0.5">
          <li>
            Disputes must go through individual arbitration (no class actions).
          </li>
          <li>Try informal resolution first (written notice + 30 days).</li>
          <li>Arbitration is governed by AAA Rules, held in Manhattan, NY.</li>
          <li>Mass claims follow a bellwether process.</li>
          <li>You can opt out within 30 days via written notice.</li>
        </ul>
      </section>

      {/* Section 14 */}
      <section>
        <h2 className="mb-1 text-sm font-semibold">14. General Legal Terms</h2>
        <ul className="list-inside list-disc space-y-0.5">
          <li>This document is the full agreement.</li>
          <li>
            Texas law governs; non-arbitrated claims go to Texas state or federal courts located in Dallas, Texas.
          </li>
          <li>Regulify.AI may audit service usage.</li>
          <li>Modifications require both parties’ written agreement.</li>
          <li>No partnership or agency is formed.</li>
          <li>
            Regulify.AI may list your name/logo in marketing unless you object.
          </li>
          <li>
            You may not transfer this Agreement without Regulify.AI’s consent.
          </li>
          <li>All notices will be sent electronically.</li>
          <li>
            Regulify.AI is not liable for delays beyond its control (force
            majeure).
          </li>
        </ul>
      </section>
    </div>
  );
};

export default ServiceAgreement;
